// src/hooks/useAuth.ts
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

type AuthState = {
  isAuthenticated: boolean;
  loading: boolean;
};

export default function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!(localStorage.getItem("access_token") ?? sessionStorage.getItem("access_token"));
  });
  const [loading, setLoading] = useState<boolean>(false);

  // login returns the server response for further handling
  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      // adapt to backend response shape: token in res.data.token or res.data.access_token
      const token = res.data?.token ?? res.data?.access_token;
      if (!token) throw new Error("No token returned from server");
      localStorage.setItem("access_token", token);
      setIsAuthenticated(true);
      return res;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    setIsAuthenticated(false);
  }, []);

  // if other tabs change auth, reflect it
  useEffect(() => {
    const s = (e: StorageEvent) => {
      if (e.key === "access_token") {
        setIsAuthenticated(!!e.newValue);
      }
    };
    window.addEventListener("storage", s);
    return () => window.removeEventListener("storage", s);
  }, []);

  return { isAuthenticated, loading, login, logout };
}
